import { createApp } from '@backstage/frontend-defaults';
import { createFrontendModule, PageBlueprint, PageLayout } from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint, SwappableComponentBlueprint } from '@backstage/plugin-app-react';
import {
  CatalogFilterBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import authPlugin from '@backstage/plugin-auth';
import { Navigate } from 'react-router-dom';
import { navModule } from './modules/nav';
import { alkemeThemeModule } from './modules/theme/AlkemeTheme';

// Microsoft (Entra) sign-in page. Replaces the default guest-only sign-in
// card shown by @backstage/frontend-defaults.
//
// The provider id "microsoft-auth-provider" must match the case-insensitive
// segment Backstage uses internally. The apiRef is wired by the auth plugin
// against the backend's `auth.providers.microsoft.production` config block
// in app-config.production.yaml.
const microsoftSignInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => {
      const { SignInPage } = await import('@backstage/core-components');
      return props => (
        <SignInPage
          {...props}
          auto
          provider={{
            id: 'microsoft-auth-provider',
            title: 'Microsoft',
            message: 'Sign in to the IT Portal with your Alkeme account',
            apiRef: microsoftAuthApiRef,
          }}
        />
      );
    },
  },
});

const signInModule = createFrontendModule({
  pluginId: 'app',
  extensions: [microsoftSignInPage],
});

// Root redirect — Backstage's catalog page is hardcoded to /catalog in the
// PageBlueprint factory (no configSchema override). Without something
// mounted at '/', the bare URL `cmdb.dev.aca.alkemeins.com/` shows the
// React 404 fallback. Mount a tiny page extension at '/' that immediately
// navigates to /catalog so the bookmark + cold-tab path lands somewhere
// useful.
const rootRedirectPage = PageBlueprint.make({
  name: 'root-redirect',
  params: {
    path: '/',
    loader: async () => <Navigate to="/catalog" replace />,
  },
});

// Wave 4.1 (2026-05-21): Agencies overview page. Top-level sidebar nav
// item. Sortable table of every agency with inline user/endpoint/
// open-signal counts. Click a row -> /agencies/<name> detail page.
const agenciesPage = PageBlueprint.make({
  name: 'agencies',
  params: {
    path: '/agencies',
    loader: async () => {
      const { AgenciesPage } = await import('./modules/cmdb/AgenciesPage');
      return <AgenciesPage />;
    },
  },
});

// 2026-05-21: Software inventory page. Top-level sidebar item.
// Sortable table of every distinct software product across the fleet,
// with installed-count + active-usage-7d + vulnerability counts.
// Fetches from /cis?ci_type=software_product so no new API surface.
const softwarePage = PageBlueprint.make({
  name: 'software',
  params: {
    path: '/software',
    loader: async () => {
      const { SoftwarePage } = await import('./modules/cmdb/SoftwarePage');
      return <SoftwarePage />;
    },
  },
});

// 2026-07-29: Finance / IT-spend page. Top-level sidebar item. One row per
// NetSuite accounting line for the IT GL universe with every captured
// dimension (vendor, category, agency class, department, memo) + signed
// amount, plus server-side rollups. Backed by GET /it-spend/lines.
//
// RESTORED 2026-07-30 — the operator reported "finance tab is missing in dev"
// even though FinancePage.tsx and the whole API side were deployed. Cause: the
// 2026-07-29 revert that moved Finance off main (4d2de879) is an ANCESTOR of
// feat/finance-it-spend, so when the branch was re-merged with main the FILE
// came back but this registration stayed deleted — leaving an orphan component
// nothing imports. Same bad-merge casualty class as the `it-spend` CsvKind that
// was dropped and restored in ea5e90e4; that one was fixed without enumerating
// the rest, which is exactly why this survived.
//
// Guard: financeHelpers.test.ts now asserts App.tsx registers this page, so an
// orphaned page component fails CI instead of shipping an invisible feature.
const financePage = PageBlueprint.make({
  name: 'finance',
  params: {
    path: '/finance',
    loader: async () => {
      const { FinancePage } = await import('./modules/cmdb/FinancePage');
      return <FinancePage />;
    },
  },
});

// 2026-09-11: APIs page — the APIM inventory surface. Backed by GET /apim/apis
// and /apim/services, which project data the ARG collector's APIM walk has been
// landing for months with nothing to read it.
//
// ONE route with ?view= tabs rather than one route per view: every extra route
// is a third registration point (blueprint + module array + SidebarItem), and
// the orphan-page failure that hid the Finance tab twice happened precisely
// because registration is multi-point. apiHelpers.test.ts asserts BOTH
// registrations below plus the SidebarItem.
const apisPage = PageBlueprint.make({
  name: 'apis',
  params: {
    path: '/apis',
    loader: async () => {
      const { ApisPage } = await import('./modules/cmdb/ApisPage');
      return <ApisPage />;
    },
  },
});

// Keyed on the CI id, NOT the API name: APIM API names are not unique across
// gateways (echo-api exists in alkeme-apim-dev and alkeme-apim-qa), so a
// name-keyed route would serve one gateway's API for another's.
const apiDetailPage = PageBlueprint.make({
  name: 'api-detail',
  params: {
    path: '/apis/:ciId',
    loader: async () => {
      const { ApiDetailPage } = await import('./modules/cmdb/ApiDetailPage');
      return <ApiDetailPage />;
    },
  },
});

// Agency Migration tracker (2026-09-28) — its own left-nav item, directly below
// Change Mgmt at the operator's request. Blueprint + module-array entry +
// SidebarItem: all three, or the page ships invisible (the Finance failure,
// twice). agencyMigrationHelpers.test.ts asserts all three.
const agencyMigrationPage = PageBlueprint.make({
  name: 'agency-migrations',
  params: {
    path: '/agency-migrations',
    loader: async () => {
      const { AgencyMigrationPage } = await import('./modules/cmdb/AgencyMigrationPage');
      return <AgencyMigrationPage />;
    },
  },
});

// Continuity mock (2026-10-09, dev only) — the continuity / risk / vendor
// register prototype with real data. The data is NOT in the bundle: the page
// fetches it from cmdb-api (dev-only, group-gated) and hands it to a public
// code-only shell in an iframe. Blueprint + module-array entry + SidebarItem,
// all three; continuityHelpers.test.ts asserts them.
const continuityPage = PageBlueprint.make({
  name: 'continuity',
  params: {
    path: '/continuity',
    loader: async () => {
      const { ContinuityPage } = await import('./modules/cmdb/ContinuityPage');
      return <ContinuityPage />;
    },
  },
});

// 2026-06-02: Hardware overview page. Top-level sidebar item.
// Sortable + searchable table of every active endpoint CI with
// Defender + Intune posture inline, agency + primary user joined,
// open-signal counts. Backed by GET /endpoints/rollup (one round-trip).
// Click a row -> /hardware/<slug> detail page.
const hardwarePage = PageBlueprint.make({
  name: 'hardware',
  params: {
    path: '/hardware',
    loader: async () => {
      const { HardwarePage } = await import('./modules/cmdb/HardwarePage');
      return <HardwarePage />;
    },
  },
});

// 2026-06-02: Per-device detail page. Reached by clicking a row on
// /hardware. Shows Defender + Intune + hardware + OS + open risk
// signals for one endpoint, with links back to agency/user.
const hardwareDetailPage = PageBlueprint.make({
  name: 'hardware-detail',
  params: {
    path: '/hardware/:name',
    loader: async () => {
      const { HardwareDetailPage } = await import('./modules/cmdb/HardwareDetailPage');
      return <HardwareDetailPage />;
    },
  },
});

// 2026-05-21: Per-agency detail page. Replaces the previous
// EntityContentBlueprint Members tab. Agencies no longer live as
// catalog Group entities — they live in CMDB and render through this
// dedicated /agencies/:name route. URLs under /agencies/* naturally
// activate the Agencies sidebar item.
const agencyDetailPage = PageBlueprint.make({
  name: 'agency-detail',
  params: {
    path: '/agencies/:name',
    loader: async () => {
      const { AgencyDetailPage } = await import('./modules/cmdb/AgencyDetailPage');
      return <AgencyDetailPage />;
    },
  },
});

// 2026-06-12: Change/Release Management (POC) — native change-request
// surface in the CMDB portal, in parallel with the ADO Boards path.
//   /changes      list (filter by status) + "New Change Request" button
//   /changes/new  create form (with a live CMDB CI picker)
//   /changes/:id  detail + state transitions + approve/reject
// Backed by the /change-requests* endpoints on ca-cmdb-api-{env}.
const changeMgmtPage = PageBlueprint.make({
  name: 'change-mgmt',
  params: {
    path: '/changes',
    loader: async () => {
      const { ChangeMgmtPage } = await import('./modules/cmdb/ChangeMgmtPage');
      return <ChangeMgmtPage />;
    },
  },
});

const newChangeRequestPage = PageBlueprint.make({
  name: 'change-mgmt-new',
  params: {
    path: '/changes/new',
    loader: async () => {
      const { NewChangeRequestPage } = await import('./modules/cmdb/NewChangeRequestPage');
      return <NewChangeRequestPage />;
    },
  },
});

const changeDetailPage = PageBlueprint.make({
  name: 'change-mgmt-detail',
  params: {
    path: '/changes/:changeId',
    loader: async () => {
      const { ChangeDetailPage } = await import('./modules/cmdb/ChangeDetailPage');
      return <ChangeDetailPage />;
    },
  },
});

// 2026-06-12: Governance page — CRM spec v1.1 §9 permission model (the 6 Entra
// groups + ADO permissions) rendered read-only from GET /change-permissions.
const governancePage = PageBlueprint.make({
  name: 'governance',
  params: {
    path: '/governance',
    loader: async () => {
      const { GovernancePage } = await import('./modules/cmdb/GovernancePage');
      return <GovernancePage />;
    },
  },
});

// 2026-06-19: Docs — the Change Release Management wiki rendered NATIVELY in the
// portal (successor to the decommissioned ADO project wiki). Replaces TechDocs,
// which couldn't reliably serve /docs on this portal (its catalog-entity HTTP
// read intermittently premature-closed). The 7 wiki pages are bundled at build
// time (scripts/gen-wiki-content.mjs -> wikiContent.ts) and rendered by
// WikiPage.tsx — zero catalog/techdocs/DB dependency.
//   /docs        -> the wiki home (index)
//   /docs/:slug  -> a specific page (filing, state-machine, field-reference, ...)
const wikiPage = PageBlueprint.make({
  name: 'wiki',
  params: {
    path: '/docs',
    loader: async () => {
      const { WikiPage } = await import('./modules/cmdb/WikiPage');
      return <WikiPage />;
    },
  },
});

const wikiDetailPage = PageBlueprint.make({
  name: 'wiki-detail',
  params: {
    path: '/docs/:slug',
    loader: async () => {
      const { WikiPage } = await import('./modules/cmdb/WikiPage');
      return <WikiPage />;
    },
  },
});

// Remove the redundant per-page chrome header (2026-06-25). PageBlueprint wraps
// every page in the swappable `core.page-layout` (PageLayout), which renders a
// header row of {icon}{title} ABOVE the page's own <Header>. title falls back to
// the plugin id — and since these pages live under the synthetic 'app' plugin
// (createFrontendModule can't carry a title), it rendered a stray "app" pill with
// a generic icon on top of every page. Every page already renders its own real
// <Header>, so that pill is pure duplicate chrome. Swap PageLayout to render only
// its children (no header). Verified safe: the catalog entity pages render their
// tab strip via EntityLayout/RoutedTabs (NOT core.page-layout's tabs), so their
// Blast Radius / Risk / Change-history tabs are unaffected. `noHeader` is a no-op
// in @backstage/frontend-plugin-api 0.16.2, so the component swap is the lever.
const minimalPageLayout = SwappableComponentBlueprint.make({
  name: 'page-layout',
  // This blueprint takes its params via a defineParams callback (advanced types).
  params: defineParams =>
    defineParams({
      component: PageLayout,
      // props is inferred as PageLayoutProps; render only the page body (drop the
      // {icon}{title} header row that produced the stray "app" pill).
      loader: () => props => <>{props.children}</>,
    }),
});

const rootRedirectModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    rootRedirectPage,
    agenciesPage,
    agencyDetailPage,
    softwarePage,
    financePage,
    apisPage,
    apiDetailPage,
    hardwarePage,
    hardwareDetailPage,
    changeMgmtPage,
    newChangeRequestPage,
    changeDetailPage,
    agencyMigrationPage,
    continuityPage,
    governancePage,
    wikiPage,
    wikiDetailPage,
    minimalPageLayout,
  ],
});

// PR #18 Phase B — Blast Radius entity content tab.
// Only attaches to Resource entities (the kind the CMDB Entity Provider emits).
const blastRadiusContent = EntityContentBlueprint.make({
  name: 'blast-radius',
  params: {
    path: '/blast-radius',
    title: 'Blast Radius',
    loader: async () => {
      const { BlastRadiusContent } = await import('./modules/cmdb/BlastRadiusContent');
      return <BlastRadiusContent />;
    },
    filter: 'kind:resource',
  },
});

// CI Attributes tab — surfaces the per-CI detail from GET /cis/{id}:
// identity columns, the source-specific JSON attributes blob, and the
// discovery-source registrations. Pairs with the Blast Radius tab so
// every CMDB-derived Resource has its full record visible in the UI.
const ciAttributesContent = EntityContentBlueprint.make({
  name: 'ci-attributes',
  params: {
    path: '/attributes',
    title: 'Attributes',
    loader: async () => {
      const { CiAttributesContent } = await import('./modules/cmdb/CiAttributesContent');
      return <CiAttributesContent />;
    },
    filter: 'kind:resource',
  },
});

// Risk tab — surfaces open Defender for Cloud findings (and future hygiene
// signals) for the current CI by calling GET /cis/{id}/risk-signals via the
// /cmdb proxy. Severity-coded chip + inline remediation guidance so operators
// get the next-step playbook without bouncing to the Azure portal.
const riskContent = EntityContentBlueprint.make({
  name: 'risk',
  params: {
    path: '/risk',
    title: 'Risk',
    loader: async () => {
      const { RiskContent } = await import('./modules/cmdb/RiskContent');
      return <RiskContent />;
    },
    filter: 'kind:resource',
  },
});

// Change history tab — surfaces deploys + manual edits that touched this CI
// by calling GET /cis/{id}/changes via the /cmdb proxy. Data populated by
// the _record-deploy.yml reusable workflow on every successful (and failed)
// deploy, plus the ARM activity log collector (Phase 1 Day 2) for
// out-of-band portal edits.
const changeHistoryContent = EntityContentBlueprint.make({
  name: 'change-history',
  params: {
    path: '/change-history',
    title: 'Change history',
    loader: async () => {
      const { ChangeHistoryContent } = await import('./modules/cmdb/ChangeHistoryContent');
      return <ChangeHistoryContent />;
    },
    filter: 'kind:resource',
  },
});

// Kind filter override — restrict the catalog left-rail Kind dropdown to
// Resource + Group only. The default extension (catalog-filter:catalog/kind
// in @backstage/plugin-catalog/alpha) hard-codes initialFilter='component'
// and doesn't expose the EntityKindPicker's `allowedKinds` prop via config,
// so the dropdown shows every kind the picker has internally (Group/Location/
// Resource/User/component) regardless of catalog.rules or what entities are
// actually present. Registering a filter with the same name='kind' under
// pluginId='catalog' overrides the default at the extension-id level
// (`catalog-filter:catalog/kind`).
const customKindFilter = CatalogFilterBlueprint.make({
  name: 'kind',
  params: {
    loader: async () => {
      const { EntityKindPicker } = await import('@backstage/plugin-catalog-react');
      return (
        <EntityKindPicker
          allowedKinds={['Resource', 'Group']}
          initialFilter="resource"
        />
      );
    },
  },
});

const cmdbModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    blastRadiusContent,
    ciAttributesContent,
    riskContent,
    changeHistoryContent,
    customKindFilter,
  ],
});

export default createApp({
  features: [catalogPlugin, authPlugin, navModule, signInModule, rootRedirectModule, cmdbModule, alkemeThemeModule],
});
